// Support, Taxes, Parties, QBID and Tax Data tabs.
import type { PartyIndex } from '@divorceos/schema';
import { useCaseView } from '../caseView';
import { amt } from '../model';
import { anyStateAdjusted, isStateAdjusted, stateAdjOf, stateAdjText } from '../engineExt';
import { useDis } from '../store';
import { Sec } from './form';
import { CsSettings, DepartureSettings, LongTermSpousalSettings, PartySettings, ReimbursementSettings, SsSettings, TacticSettings, TaxSettings } from './Sections';
import { FORM_TITLES, groupLines, lineLabel } from '../taxForms';

export function SupportTab() {
  return <><CsSettings /><SsSettings /><LongTermSpousalSettings /><DepartureSettings /><ReimbursementSettings /><TacticSettings /></>;
}

export function TaxesTab() {
  const setUi = useDis((s) => s.setUi);
  return (
    <>
      <TaxSettings />
      <Sec title="Tax forms">
        <p style={{ margin: '0 0 8px' }} className="muted">See the federal and California form lines the calculation uses, Guideline and Proposed side by side, for either party.</p>
        <button type="button" className="btn" onClick={() => setUi({ dialog: 'forms' })}>Open tax form viewer (W)</button>
      </Sec>
    </>
  );
}

export const PartiesTab = PartySettings;

export { QbidTab } from './Qbid';

export function TaxDataTab() {
  const { c, labels, result } = useCaseView();
  if (!result) return <Sec title="Tax data"><p className="muted">No result yet.</p></Sec>;
  const fed = [0, 1].map((p) => result.parties[p as PartyIndex].guideline.federal.lines);
  const st = [0, 1].map((p) => result.parties[p as PartyIndex].guideline.state.lines);
  const block = (title: string, a: Record<string, number>, b: Record<string, number>) => {
    const groups = groupLines(a, b);
    return (
      <Sec title={title} key={title}>
        <table className="tbl">
          <thead><tr><th>Form / line</th><th className="n">{labels[0]}</th><th className="n">{labels[1]}</th></tr></thead>
          <tbody>
            {groups.map((g) => [
              <tr key={g.form}><td colSpan={3} style={{ fontWeight: 500, background: '#fff' }}>{FORM_TITLES[g.form] ?? g.form}</td></tr>,
              ...g.keys.map((k) => <tr key={k}><td style={{ paddingLeft: 20 }}>{lineLabel(k)}</td><td className="n">{fmtLine(k, a[k])}</td><td className="n">{fmtLine(k, b[k])}</td></tr>),
            ])}
          </tbody>
        </table>
      </Sec>
    );
  };
  return (
    <>
      <div className="sec"><p className="muted" style={{ margin: 0 }}>Annual figures from the {c.taxYear} guideline calculation, published IRS and FTB tables. Proposed-column lines are in the tax form viewer (W).</p></div>
      {anyStateAdjusted(c) && <StateTaxBlock />}
      {block('Federal', fed[0] ?? {}, fed[1] ?? {})}
      {block('California', st[0] ?? {}, st[1] ?? {})}
    </>
  );
}

export function fmtLine(k: string, v: number | undefined): string {
  if (v === undefined) return '—';
  if (/marginal/i.test(k)) return `${(v * (v <= 1 ? 100 : 1)).toFixed(1)}%`;
  const r = Math.round(v);
  return r < 0 ? `(${Math.abs(r).toLocaleString('en-US')})` : r.toLocaleString('en-US');
}

/** Computed and adjusted California income tax per party (guideline column), in the case period. */
function StateTaxBlock() {
  const { c, labels, result } = useCaseView();
  if (!result) return null;
  const rows = ([0, 1] as PartyIndex[]).map((p) => {
    const a = stateAdjOf(c.parties[p]);
    const t = result.parties[p].guideline.taxes.stateIncome;
    const deltaAnnual = result.parties[p].guideline.state.lines.stateTaxAdjustment;
    // The engine's taxes carry the adjustment; lines.stateTaxAdjustment is the annual change.
    const computed = typeof deltaAnnual === 'number' ? t - deltaAnnual / 12 : t;
    const adjusted = t;
    return { a, computed, adjusted, on: isStateAdjusted(a) && c.parties[p].taxSettings.stateIncomeTax };
  });
  return (
    <Sec title="State tax adjustment">
      <table className="tbl">
        <thead><tr><th>Guideline, {c.mode === 'ANNUAL' ? 'yearly' : 'monthly'}</th><th className="n">{labels[0]}</th><th className="n">{labels[1]}</th></tr></thead>
        <tbody>
          <tr><td>California income tax as computed</td>{rows.map((r, i) => <td key={i} className="n">{amt(c, r.computed)}</td>)}</tr>
          <tr><td>Adjustment</td>{rows.map((r, i) => <td key={i} className="n">{r.on ? stateAdjText(r.a) : 'none'}</td>)}</tr>
          <tr style={{ fontWeight: 600 }}><td>Adjusted state tax (used for support)</td>{rows.map((r, i) => <td key={i} className="n">{amt(c, r.on ? r.adjusted : r.computed)}</td>)}</tr>
        </tbody>
      </table>
    </Sec>
  );
}
